import { lazy, type ReactNode } from 'react';
import type { FeatureFlagKey } from '@core/utils/flags/keys';

import { homeSpa } from '@hf/home/spaRouter';
import { tearsheetsSpa } from '@hf/tearsheets/spaRouter';
import { omsSpa } from '@hf/oms/spaRouter';
import { adminSpa } from '@hf/admin/spaRouter';
import { settingsSpa } from '@hf/settings/spaRouter';

export const LOGIN_NEXT_PARAM = 'next';

export type RouteMeta = {
  path: string;
  label: string;
  icon?: ReactNode;
  public: boolean; // Public routes are flat (no layout + children).
  nav?: 'main' | 'footer'; // Where the item shows on the navbar.
  trackRecent?: boolean; // Track recent items for the home page.
  adminOnly?: boolean; // Only show to admins / wrap with RequireAdmin.
  featureFlag?: FeatureFlagKey; // Shell withholds the outlet until this flag has been evaluated.
};

export type SpaChildRoute = { index: true; path?: undefined; element: ReactNode } | { index?: false; path: string; element: ReactNode };

/**
 * Feature SPA registry — each `apps/<name>/src/spaRouter.tsx` exports one of these.
 * Core aggregates them; it does not import page modules directly.
 */
export type SpaDefinition = RouteMeta & {
  key: string;
  public: false;
  /** Relative segments under `path` (may be `{}`). */
  paths: Readonly<Record<string, string>>;
  layout: ReactNode;
  children: readonly SpaChildRoute[];
};

type PublicRouteDefinition = RouteMeta & {
  key: string;
  public: true;
  element: ReactNode;
};

/**
 * Feature SPAs — declared once; all derived exports come from this list.
 * Array order is nav order (main then footer within each `nav` group).
 * Add the import above, then append here.
 */
const FEATURE_SPAS = [homeSpa, tearsheetsSpa, omsSpa, adminSpa, settingsSpa] as const;

const LoginPage = lazy(async () => {
  const mod = await import('../pages/LoginPage');
  return { default: mod.LoginPage };
});

const loginRoute = {
  key: 'login',
  path: '/login',
  label: 'Login',
  public: true,
  element: <LoginPage />,
} as const satisfies PublicRouteDefinition;

const ALL_MOUNTS = [loginRoute, ...FEATURE_SPAS] as const;

function toMeta(mount: RouteMeta): RouteMeta {
  return {
    path: mount.path,
    label: mount.label,
    icon: mount.icon,
    public: mount.public,
    nav: mount.nav,
    trackRecent: mount.trackRecent,
    adminOnly: mount.adminOnly,
    featureFlag: mount.featureFlag,
  };
}

export const ROUTES_META = Object.fromEntries(ALL_MOUNTS.map((mount) => [mount.key, toMeta(mount)])) as {
  [M in (typeof ALL_MOUNTS)[number] as M['key']]: RouteMeta;
};

type Paths = { [M in (typeof ALL_MOUNTS)[number] as M['key']]: M['path'] };
export const PATHS = Object.fromEntries(ALL_MOUNTS.map((mount) => [mount.key, mount.path])) as Paths;

export function safeInternalPath(value: string | null): string | undefined {
  if (!value || !value.startsWith('/')) return undefined;
  if (value.startsWith('//') || value.startsWith('/\\')) return undefined;
  return value;
}

/** Public flat routes derived from mounts marked `public`. */
export const publicRoutes = ALL_MOUNTS.flatMap((mount) => (mount.public && 'element' in mount ? [{ path: mount.path, element: mount.element }] : []));

/** Authenticated nested SPAs — layout + children from each spa registry. */
export const privateNestedRoutes = FEATURE_SPAS.map((spa) => ({
  path: spa.path,
  layout: spa.layout,
  children: [...spa.children],
  adminOnly: 'adminOnly' in spa && Boolean(spa.adminOnly),
}));
